import React, { useEffect, useState } from 'react'
import { Search, HelpCircle } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Textarea } from '@/components/ui/textarea'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { cn } from '@/lib/utils'

// Ready-made searches, like Jira's default filters
export const QUICK_FILTERS = [
  { label: 'My open issues', jql: 'assignee = currentUser() AND statusCategory != Done ORDER BY priority DESC, updated DESC' },
  { label: 'Reported by me', jql: 'reporter = currentUser() ORDER BY created DESC' },
  { label: 'Watching', jql: 'watcher = currentUser() AND statusCategory != Done ORDER BY updated DESC' },
  { label: 'Updated this week', jql: 'updated >= -7d ORDER BY updated DESC' },
  { label: 'Overdue', jql: 'due < startOfDay() AND statusCategory != Done ORDER BY due' },
  { label: 'Unassigned', jql: 'assignee IS EMPTY AND statusCategory != Done ORDER BY created DESC' }
]

const HELP = [
  ['Fields', 'project, key, assignee, reporter, watcher, status, statusCategory, type, priority, labels, parent, sprint, storyPoints, text, summary, description, created, updated, due'],
  ['Sprints', 'sprint IN openSprints(), sprint IN futureSprints(), sprint IN closedSprints(), sprint = "PC Sprint 3", sprint IS EMPTY'],
  ['Operators', '=  !=  >  >=  <  <=  ~ (contains)  !~  IN (…)  NOT IN (…)  IS EMPTY  IS NOT EMPTY'],
  ['Combine', 'AND, OR, NOT and ( ) — put values with spaces in quotes: status = "In Review"'],
  ['People', 'currentUser(), an email, a name or a user id'],
  ['Dates', '2026-10-01, -7d, -2w, -4h, now(), startOfDay(), startOfDay(-1), startOfWeek(), startOfMonth()'],
  ['Order', 'ORDER BY priority DESC, updated, created, due, key, status, summary, rank (backlog order), storyPoints']
]

const EXAMPLES = [
  'project = PC AND type = Bug AND statusCategory != Done',
  'labels IN (backend, api) AND priority >= high',
  'text ~ "login" AND updated >= startOfWeek()',
  'parent = PC-42 ORDER BY key',
  'sprint IN openSprints() AND assignee = currentUser() ORDER BY rank'
]

// JQL input with quick filters and a syntax cheat sheet. Enter runs the search.
const JqlFilterBar = ({ value, onSearch, error, loading }) => {
  const [draft, setDraft] = useState(value || '')
  useEffect(() => { setDraft(value || '') }, [value])

  const run = (jql = draft) => onSearch(jql.trim())

  return (
    <div className="space-y-2">
      <div className="flex flex-wrap gap-1.5">
        {QUICK_FILTERS.map(f => (
          <button
            key={f.label}
            type="button"
            onClick={() => run(f.jql)}
            className={cn(
              'rounded-full border px-2.5 py-0.5 text-xs transition-colors',
              value === f.jql ? 'border-primary bg-primary/10 text-primary' : 'text-muted-foreground hover:text-foreground hover:bg-accent'
            )}
          >
            {f.label}
          </button>
        ))}
      </div>
      <div className="flex items-start gap-2">
        <Textarea
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault()
              run()
            }
          }}
          rows={1}
          spellCheck={false}
          placeholder='project = PC AND assignee = currentUser() AND statusCategory != Done ORDER BY priority DESC'
          aria-label="JQL query"
          className={cn('min-h-[38px] font-mono text-sm resize-y', error && 'border-destructive focus-visible:ring-destructive')}
        />
        <Button onClick={() => run()} disabled={loading} className="h-[38px] shrink-0">
          <Search className="h-4 w-4 mr-1.5" /> Search
        </Button>
        <Popover>
          <PopoverTrigger asChild>
            <Button variant="outline" size="icon" className="h-[38px] w-[38px] shrink-0" title="JQL syntax help">
              <HelpCircle className="h-4 w-4" />
            </Button>
          </PopoverTrigger>
          <PopoverContent align="end" className="w-[28rem] max-w-[calc(100vw-2rem)] text-xs space-y-2">
            <p className="font-medium text-sm">Searching with JQL</p>
            <dl className="space-y-1.5">
              {HELP.map(([term, text]) => (
                <div key={term}>
                  <dt className="font-medium">{term}</dt>
                  <dd className="text-muted-foreground font-mono">{text}</dd>
                </div>
              ))}
            </dl>
            <p className="font-medium pt-1">Examples (click to use)</p>
            <div className="space-y-1">
              {EXAMPLES.map(example => (
                <button key={example} type="button" onClick={() => run(example)} className="block w-full text-left font-mono rounded px-1.5 py-1 hover:bg-accent">
                  {example}
                </button>
              ))}
            </div>
          </PopoverContent>
        </Popover>
      </div>
      {error && <p className="text-sm text-destructive" role="alert">{error}</p>}
    </div>
  )
}

export default JqlFilterBar
